import environment from '../environment';
import { UserOrderDetails } from '../../features/order-history/order-history.interface';
import { PROFESSIONAL } from '../utils/util';

export const BOARDS_BEYOND_FALLBACK_SSO_URL = `${environment().boardsBeyondUrl}/sso`;

export enum ProfessionalPrograms {
  // first aid forward
  MKTSP0004700 = 'MKTSP-000470-0',
  MKTSP0004701 = 'MKTSP-000470-1',
  MKTSP0004702 = 'MKTSP-000470-2',
  // boards beyond
  MKTSP009971000 = 'MKTSP-009971-000',
  MKTSP009971001 = 'MKTSP-009971-001',
  MKTSP009971002 = 'MKTSP-009971-002',
  MKTSP009971003 = 'MKTSP-009971-003',
  MKTSP009971004 = 'MKTSP-009971-004',
  // clinical reasoning
  MKTSP000471001 = 'MKTSP-000471-001',
}

export const professionalSteps = {
    [ProfessionalPrograms.MKTSP0004701]: {
        step: "Step 1"
    },
    [ProfessionalPrograms.MKTSP0004702]: {
        step: "Step 2"
    },
    [ProfessionalPrograms.MKTSP009971001]: {
        step: "Step 1 Preclinical"
    },
    [ProfessionalPrograms.MKTSP009971002]: {
        step: "Step 2-3 Clinical"
    },
    [ProfessionalPrograms.MKTSP009971003]: {
        step: "Clinical Confidence"
    },
    [ProfessionalPrograms.MKTSP009971004]: {
        step: "The OMM Series"
    },
    [ProfessionalPrograms.MKTSP000471001]: {
      step: "Clinical Reasoning"
    },
}

type ProfessionalRedirects = {
    [key in ProfessionalPrograms]: {
        appUrl?: string;
        plp: string;
    }
}

export const professionalRedirects: ProfessionalRedirects = {
    [ProfessionalPrograms.MKTSP0004700]: {
        appUrl: "step-one", // The top level first aid forward program can go to microsite page
        plp: "/professional/program/first-aid-forward.html",
    },
    [ProfessionalPrograms.MKTSP0004701]: {
        appUrl: "step-one",
        plp: "/professional/program/first-aid-forward/step-one.html",
    },
    [ProfessionalPrograms.MKTSP0004702]: {
        appUrl: "step-two",
        plp: "/professional/program/first-aid-forward/step-two.html",
    },
    [ProfessionalPrograms.MKTSP009971000]: {
        plp: "/professional/program/boards-beyond.html",
    },
    [ProfessionalPrograms.MKTSP009971001]: {
        plp: "/professional/program/boards-beyond/products/step-1-preclinical.html",
    },
    [ProfessionalPrograms.MKTSP009971002]: {
        plp: "/professional/program/boards-beyond/products/step-2-3-clinical.html",
    },
    [ProfessionalPrograms.MKTSP009971003]: {
        plp: "/professional/program/boards-beyond/products/clinical-confidence.html",
    },
    [ProfessionalPrograms.MKTSP009971004]: {
        plp: "/professional/program/boards-beyond/products/omm-series.html",
    },
    [ProfessionalPrograms.MKTSP000471001]: {
      plp: "/highered/digital-products/compass/clinical-reasoning.html",
    },
}

export function getProfessionalPdpLink(programId: string, seoTitle: string, isbn?: string) {
  if (professionalRedirects[programId]) {
    return getProfessionalPlpLink(programId, seoTitle);
  }
  return getMhpPdpLink(programId, seoTitle, isbn);
}

export function getProfessionalPlpLink(programId?: string, seoTitle?: string) {
    const redirect = professionalRedirects[programId || ''];
    return redirect ? redirect.plp : (programId && seoTitle) ? getMhpPdpLink(programId, seoTitle) : '';
}

export const MHP_PDP_BASE_PATH = '/highered/mhp/product/';

export const MHP_EBOOK_INSTRUCTOR_SAMPLE_MARKETO_URL = `https://learn.mheducation.com/Digital_Sample_Form_1.html`;

export function getMhpPdpLink(programId: string, seoTitle: string, isbn?: string) {
  return `${MHP_PDP_BASE_PATH}${seoTitle ?? programId}${isbn ? `?isbn=${isbn}` : ''}`;
}

export const PROFESSIONAL_FA4_EDUCATORS_URL: string = environment().mheducationUrl + '/professional/program/first-aid-forward/for-educators.html';

export const PROFESSIONAL_EDUCATORS_MHP_URL: string = environment().mheducationUrl + '/highered.html';

export const PROFESSIONAL_SITE_URL = 'https://www.mhprofessional.com/';

export const PROFESSIONAL_EDUCATORS_BOARDS_AND_BEYONDS_URL = `${PROFESSIONAL_SITE_URL}interactive-learning-solutions/boards-beyond-idr?_gl=1*1doz1be*_ga*MjAzOTM3OTMxMS4xNzI2ODY5NTU2*_ga_WF53HKQGB7*MTcyODU4OTAxNC40LjEuMTcyODU4OTczMC4wLjAuMA`;

export const PROFESSIONAL_CR_EDUCATORS_URL: string = environment().mheducationUrl + '/highered/digital-products/compass/clinical-reasoning/teaching';

export function getFa4AppUrl(programSourceId: string): string {
    return `${environment().firstAidForwadAppUrl}/${professionalRedirects[programSourceId].appUrl}`;
}

export function getClinicalReasoningAppUrl(): string {
  return environment().clinicalReasoningAppUrl;
}

export function getItemLaunchObjectUrl(orderDetail: UserOrderDetails): string {
    if (!orderDetail.launchObject) return "";
    const url = new URL(orderDetail.launchObject.url ?? "");
    (orderDetail.launchObject.params || []).forEach(({ key, value }) => url.searchParams.append(key, value));
    return url.toString();
}

export function isBoardsAndBeyondsProgram(programId: string) {
  return [
    ProfessionalPrograms.MKTSP009971000,
    ProfessionalPrograms.MKTSP009971001,
    ProfessionalPrograms.MKTSP009971002,
    ProfessionalPrograms.MKTSP009971003,
    ProfessionalPrograms.MKTSP009971004,
  ].includes(programId as ProfessionalPrograms);
}

export function isFirstAidForwardProgram(programId: string) {
  return [
    ProfessionalPrograms.MKTSP0004700,
    ProfessionalPrograms.MKTSP0004701,
    ProfessionalPrograms.MKTSP0004702,
  ].includes(programId as ProfessionalPrograms);
}

export function isClinicalReasoningProgram(programId: string) {
  return [ProfessionalPrograms.MKTSP000471001].includes(programId as ProfessionalPrograms);
}

export function isMhpProfessionalProgram(businessUnit: string, programId: string) {
  return (
    businessUnit?.toLowerCase() === PROFESSIONAL.toLowerCase() &&
    !(isFirstAidForwardProgram(programId) || isBoardsAndBeyondsProgram(programId) || isClinicalReasoningProgram(programId))
  );
}